import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import logger from '../utils/logger';
import { useParams, useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { withReferral } from "../utils/withReferral";
import { useProfile } from "../contexts/ProfileContext";
import { usePageTracking, useAnalytics } from "../hooks/useAnalytics";
import LoadingScreen from "../components/LoadingScreen";
import { showToast } from "../components/Toast";
import ShareModal from "../components/ShareModal";
import PaywallModal from "../components/PaywallModal";
import StoryShelf from "../components/library/PictureBook";

const CharacterDetail = ({ isPublic = false }) => {
  usePageTracking('Character Detail', { isPublic });
  const { trackContentView, trackContentShare, trackCharacterEdit, trackError, trackContentDeleted, trackContentDownloaded } = useAnalytics();
  const { id: characterId } = useParams();
  // Share token from the link (?token=…), used by the anonymous share view.
  const shareToken = new URLSearchParams(window.location.search).get('token');
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user, subscriptionStatus } = useAuth();
  const { requireParentalVerification } = useProfile();
  const [character, setCharacter] = useState(null);
  const [characterStories, setCharacterStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnimated, setShowAnimated] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [allCharacters, setAllCharacters] = useState({});
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);

  const loadAllCharacters = useCallback(async () => {
    try {
      const response = await apiClient.getUserCharacters(user.id);

      if (response.status) {
        const charactersData = response.data || [];
        const charactersMap = {};
        charactersData.forEach((char) => {
          charactersMap[char.id] = char;
        });
        setAllCharacters(charactersMap);
      }
    } catch (error) {
      logger.error("Failed to load characters:", error);
    }
  }, [apiClient]);

  const loadCharacter = useCallback(async () => {
    try {
      setLoading(true);
      const viewStartTime = Date.now();
      const response = isPublic
        ? await apiClient.getSharedCharacter(characterId, shareToken)
        : await apiClient.getCharacter(characterId);

      if (response.status) {
        setCharacter(response.data);
        setEditedName(response.data?.name || "");

        // Track character view
        trackContentView('character', characterId);

        // Track view duration when component unmounts
        return () => {
          const viewDuration = Math.round((Date.now() - viewStartTime) / 1000);
          if (viewDuration > 2) { // Only track if viewed for more than 2 seconds
            trackContentView('character', characterId, viewDuration);
          }
        };
      } else {
        showToast("Failed to load character", "error");
        trackError('character_load_failed', response.error || 'Unknown error', { characterId });
      }
    } catch (error) {
      logger.error("Failed to load character:", error);
      showToast("Failed to load character", "error");
      trackError('character_load_error', error.message, { characterId });
    } finally {
      setLoading(false);
    }
  }, [apiClient, characterId, isPublic, trackContentView, trackError]);

  const loadCharacterStories = useCallback(async () => {
    if (!characterId) return;

    try {
      logger.log("🔍 Loading stories for character:", characterId);
      const response = await apiClient.getUserStories();
      if (response.status) {
        const allStories = response.data || [];
        logger.log("📊 All user stories:", allStories);

        // Log the structure of stories to debug
        if (allStories.length > 0) {
          logger.log(
            "🔬 First story structure (full object):",
            JSON.stringify(allStories[0], null, 2)
          );
          logger.log("🔬 Story keys:", Object.keys(allStories[0]));

          // Check all possible character-related properties
          const firstStory = allStories[0];
          if (firstStory.characters) {
            logger.log("👥 story.characters:", firstStory.characters);
          }
          if (firstStory.character_ids) {
            logger.log("👥 story.character_ids:", firstStory.character_ids);
          }
          if (firstStory.characterIds) {
            logger.log("👥 story.characterIds:", firstStory.characterIds);
          }
        }

        // Try multiple filtering approaches to see which one works
        logger.log(
          "🎯 Target character ID:",
          characterId,
          "type:",
          typeof characterId
        );

        // Approach 1: Filter by story.characters array (current approach)
        const filteredStories1 = allStories.filter((story) => {
          if (!story.characters || !Array.isArray(story.characters))
            return false;

          return story.characters.some((char) => {
            const match =
              char.id === characterId ||
              char.id === parseInt(characterId) ||
              char.character_id === characterId ||
              char.character_id === parseInt(characterId) ||
              char === characterId ||
              char === parseInt(characterId);

            if (match) {
              logger.log("✅ MATCH FOUND in approach 1:", {
                story: story.title || story.name,
                char: char,
                characterId: characterId,
              });
            }
            return match;
          });
        });

        // Approach 2: Filter by character_ids array
        const filteredStories2 = allStories.filter((story) => {
          if (!story.character_ids || !Array.isArray(story.character_ids))
            return false;

          const match =
            story.character_ids.includes(characterId) ||
            story.character_ids.includes(parseInt(characterId)) ||
            story.character_ids.includes(characterId.toString());

          if (match) {
            logger.log("✅ MATCH FOUND in approach 2:", {
              story: story.title || story.name,
              character_ids: story.character_ids,
              characterId: characterId,
            });
          }
          return match;
        });

        // Approach 3: Check if there are any other character reference patterns
        const filteredStories3 = allStories.filter((story) => {
          // Look for any property that might reference this character
          const storyStr = JSON.stringify(story).toLowerCase();
          const match =
            storyStr.includes(characterId.toString()) ||
            storyStr.includes(`"${characterId}"`);

          if (match) {
            logger.log("✅ MATCH FOUND in approach 3 (string search):", {
              story: story.title || story.name,
              characterId: characterId,
            });
          }
          return match;
        });

        logger.log("🔍 Results summary:", {
          approach1Count: filteredStories1.length,
          approach2Count: filteredStories2.length,
          approach3Count: filteredStories3.length,
          totalStories: allStories.length,
        });

        // Use the approach that found results, prioritizing the most reliable one
        let finalStories = [];
        if (filteredStories1.length > 0) {
          finalStories = filteredStories1;
          logger.log("✅ Using approach 1 (story.characters)");
        } else if (filteredStories2.length > 0) {
          finalStories = filteredStories2;
          logger.log("✅ Using approach 2 (story.character_ids)");
        } else if (filteredStories3.length > 0) {
          finalStories = filteredStories3;
          logger.log("✅ Using approach 3 (string search)");
        } else {
          logger.log("❌ No matching stories found with any approach");
        }

        logger.log("✅ Final filtered character stories:", finalStories);
        setCharacterStories(finalStories);
      } else {
        logger.error("Failed to load stories:", response.error);
        showToast("Failed to load stories", "error");
      }
    } catch (error) {
      logger.error("Failed to load character stories:", error);
      showToast("Failed to load character stories", "error");
    }
  }, [apiClient, user?.id, characterId]);

  useEffect(() => {
    if (characterId && apiClient) {
      loadCharacter();
      if (!isPublic) {
        loadAllCharacters();
      }
    }
  }, [characterId, apiClient, loadCharacter, loadAllCharacters, isPublic]);

  useEffect(() => {
    if (characterId && apiClient && user?.id && !isPublic) {
      loadCharacterStories();
    }
  }, [characterId, apiClient, user?.id, loadCharacterStories, isPublic]);

  const getCharacterNames = (story) => {
    if (!story.characters || story.characters.length === 0) return "";

    // Handle both character IDs and character objects
    const names = story.characters
      .map((char) => {
        // If char is an object with a name property, use it
        if (typeof char === "object" && char.name) {
          return char.name;
        }
        // If char is an ID (string or number), look it up in the allCharacters map
        const characterData = allCharacters[char];
        return characterData ? characterData.name : null;
      })
      .filter(Boolean); // Remove any null/undefined values

    return names.join(", ");
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName.trim() === character.name) {
      setIsEditingName(false);
      return;
    }

    try {
      setIsUpdating(true);
      const response = await apiClient.updateCharacter(character.id, {
        name: editedName.trim(),
      });

      // Check for name safety error
      if (response.status === "name_inappropriate" || response.error === "name_inappropriate") {
        const errorMessage = response.message || "This name contains inappropriate content. Please choose a different name.";
        showToast(errorMessage, "error");
        // Reset to original name
        setEditedName(character.name);
        setIsEditingName(false);
      } else if (response.status || response.success) {
        setCharacter((prev) => ({
          ...prev,
          name: editedName.trim(),
        }));
        setIsEditingName(false);
        showToast("Character name updated successfully!", "success");
        await loadCharacter();
      } else {
        showToast(response.error || "Failed to update character name", "error");
      }
    } catch (error) {
      showToast(error.message || "Failed to update character name", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCharacter = async () => {
    if (!window.confirm(`Are you sure you want to delete ${character.name}? This action cannot be undone and will remove the character from all stories.`)) {
      return;
    }

    // Optimistically clear character state
    const characterToDelete = character;
    setCharacter(null);

    try {
      const response = await apiClient.deleteCharacter(characterToDelete.id);
      if (response.status) {
        trackContentDeleted('character', characterToDelete.id);
        showToast('Character deleted successfully', 'success');
        
        // Add DELETECHARACTER tag to user's data_tags
        if (user?.id && apiClient) {
          const timestamp = Date.now();
          const tag = `DELETECHARACTER_${characterToDelete.id}_${timestamp}`;
          apiClient.addUserDataTag(user.id, tag).then((result) => {
            if (result.status) {
              logger.log(`✅ Added tag ${tag} for user ${user.id}`);
            } else {
              logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
            }
          }).catch((error) => {
            logger.error(`❌ Error adding tag ${tag}:`, error);
          });
        }
        
        // Dispatch event to notify Library page to refresh
        window.dispatchEvent(new CustomEvent('character:deleted', { detail: { characterId: characterToDelete.id } }));
        navigate('/library');
      } else {
        // Revert optimistic update on failure
        setCharacter(characterToDelete);
        showToast('Failed to delete character', 'error');
      }
    } catch (error) {
      // Revert optimistic update on error
      setCharacter(characterToDelete);
      logger.error('Failed to delete character:', error);
      showToast('Failed to delete character', 'error');
    }
  };

  const handleDownloadCharacter = async () => {
    // Check if user is premium
    const isPremium = ['STRIPE', 'IAP', 'EDDY', 'PREMIUM', 'NIKHIL', 'PHILIP', 'BYPASS', 'STARTER', 'PLUS', 'FAMILY', 'UNLIMITED'].includes(subscriptionStatus?.subscriptionType);

    if (!isPremium) {
      // Show paywall modal for non-premium users
      logger.log('Free user attempting character download - showing paywall');
      setIsPaywallModalOpen(true);
      return;
    }

    // Premium user - proceed with download
    if (!characterId) {
      showToast('Unable to download. Missing character information.', 'error');
      return;
    }

    logger.log('Premium user - downloading watermarked character');
    showToast('Downloading character image...', 'info');

    const result = await apiClient.downloadWatermarkedCharacter(characterId, character?.name);

    if (result.success) {
      trackContentDownloaded('character', characterId);
      showToast('Character downloaded successfully!', 'success');
      
      // Add DOWNLOAD_CHARACTER tag to user's data_tags (only on successful download)
      if (user?.id && apiClient) {
        const timestamp = Date.now();
        const tag = `DOWNLOAD_CHARACTER_${characterId}_${timestamp}`;
        apiClient.addUserDataTag(user.id, tag).then((tagResult) => {
          if (tagResult.status) {
            logger.log(`✅ Added download character tag ${tag} for user ${user.id}`);
          } else {
            logger.warn(`⚠️ Failed to add download character tag ${tag}:`, tagResult.error);
          }
        }).catch((error) => {
          logger.error(`❌ Error adding download character tag ${tag}:`, error);
        });
      }
    } else {
      showToast(result.error || 'Failed to download character', 'error');
    }
  };

  if (loading) {
    return (
      <LoadingScreen message="Loading character details..." type="character" />
    );
  }

  if (!character) {
    return (
      <div
        className="min-h-screen relative"
        style={{
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
        }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 text-center">
          <h1
            style={{
              fontFamily: "'Fredoka', sans-serif",
              color: "#0B1A33",
              fontWeight: 700,
              fontSize: "clamp(1.75rem, 3.6vw, 2.4rem)",
              letterSpacing: "-0.01em",
              textTransform: "uppercase",
              margin: "0 0 8px",
            }}
          >
            Character not found
          </h1>
          <p
            className="mb-6"
            style={{ fontFamily: "'Fredoka', sans-serif", color: "#5A6B85", fontSize: 15 }}
          >
            We couldn't find this character.
          </p>
          <button
            type="button"
            onClick={() => navigate("/library")}
            className="px-6 py-3 transition active:scale-[0.98]"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "#0065F8",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 14,
              border: "none",
              borderRadius: 999,
              boxShadow: "0px 4px 0px #073B8E",
              cursor: "pointer",
            }}
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  // ── Derived data ──
  const isPremium = ['STRIPE', 'IAP', 'EDDY', 'PREMIUM', 'NIKHIL', 'PHILIP', 'BYPASS', 'STARTER', 'PLUS', 'FAMILY', 'UNLIMITED'].includes(subscriptionStatus?.subscriptionType);
  const createdLabel = (() => {
    if (!character.created_at) return null;
    const d = new Date(character.created_at);
    const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    if (days === 0) return "Created today";
    if (days === 1) return "Created yesterday";
    if (days < 30) return `Created ${days} days ago`;
    return `Created ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  })();
  const featuredCount = characterStories.length;
  const hasAnimated = !!character.animated_image_url;
  // New characters no longer store the raw drawing/photo — handdrawn_image_url
  // just mirrors animated. Only offer the toggle when there's a genuinely
  // distinct original (legacy characters).
  const hasHanddrawn =
    !!character.handdrawn_image_url &&
    character.handdrawn_image_url !== character.animated_image_url;
  const showAnimatedResolved = hasAnimated ? showAnimated : false;
  const portraitUrl = showAnimatedResolved
    ? character.animated_image_url
    : (character.handdrawn_image_url || character.animated_image_url || character.image_url || "/assets/handdrawn-character.png");

  return (
    <div
      className="character-detail-container min-h-screen relative"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -180, left: -160, width: 480, height: 480, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -200, right: -200, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16">
        {/* Back link */}
        {!isPublic && (
          <button
            type="button"
            onClick={() => navigate("/library")}
            className="inline-flex items-center gap-2 mb-6 transition-colors"
            style={{ fontFamily: "'Fredoka', sans-serif", color: "#5A6B85", fontSize: 14, fontWeight: 600 }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Library
          </button>
        )}

        {/* ── HERO SPOTLIGHT ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[460px_1fr] gap-8 lg:gap-12 items-start">
          {/* PORTRAIT COLUMN */}
          <div>
            {/* Spotlight portrait */}
            <div
              className="relative mx-auto"
              style={{
                width: "min(420px, 100%)",
                aspectRatio: "1 / 1",
              }}
            >
              {/* Soft glow ring */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: -28,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(0,101,248,0.22) 0%, rgba(0,101,248,0.10) 40%, rgba(0,101,248,0) 70%)",
                  filter: "blur(8px)",
                }}
              />
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "#FFFFFF",
                  border: "4px solid #FFFFFF",
                  boxShadow: "0px 6px 0px #073B8E, 0px 18px 40px rgba(0,101,248,0.22)",
                }}
              >
                <img
                  src={portraitUrl}
                  alt={character.name || "Character"}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "/assets/handdrawn-character.png"; }}
                />
                {/* Sparkles */}
                {[
                  { top: "8%",  left: "10%", size: 10, delay: 0 },
                  { top: "16%", left: "82%", size: 8,  delay: 0.4 },
                  { top: "70%", left: "6%",  size: 7,  delay: 0.8 },
                  { top: "78%", left: "86%", size: 9,  delay: 1.2 },
                ].map((s, i) => (
                  <motion.span
                    key={i}
                    aria-hidden="true"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: [0, 1, 0], scale: [0.6, 1.1, 0.6] }}
                    transition={{ duration: 2.4, delay: s.delay, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      position: "absolute",
                      top: s.top,
                      left: s.left,
                      width: s.size,
                      height: s.size,
                      borderRadius: "50%",
                      background: "#FFFFFF",
                      boxShadow: "0 0 8px 2px rgba(255,255,255,0.8), 0 0 14px 4px rgba(0,202,255,0.5)",
                    }}
                  />
                ))}
              </motion.div>
            </div>

            {/* Animated / Handdrawn toggle */}
            {hasAnimated && hasHanddrawn && (
              <div
                className="mt-6 mx-auto inline-flex p-1"
                style={{
                  display: "flex",
                  background: "#FFFFFF",
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 999,
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                  width: "fit-content",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {[
                  { key: "animated", label: "Animated", on: showAnimated },
                  { key: "handdrawn", label: "Hand-drawn", on: !showAnimated },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setShowAnimated(opt.key === "animated")}
                    className="px-4 py-2 transition-all"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: "0.02em",
                      background: opt.on ? "#0065F8" : "transparent",
                      color: opt.on ? "#FFFFFF" : "#5A6B85",
                      border: "none",
                      borderRadius: 999,
                      boxShadow: opt.on ? "0px 2px 0px #073B8E" : "none",
                      cursor: "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO COLUMN */}
          <div className="flex flex-col justify-center" style={{ minHeight: "min(420px, 100%)" }}>
            {/* Small label + chip row */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: "#0065F8",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                Character
              </span>
              {createdLabel && (
                <>
                  <span style={{ color: "#8CA0BC", fontSize: 11 }}>•</span>
                  <span style={{ fontFamily: "'Fredoka', sans-serif", color: "#5A6B85", fontSize: 12, fontWeight: 600 }}>
                    {createdLabel}
                  </span>
                </>
              )}
            </div>

            {/* Name (inline editable) */}
            {!isPublic && isEditingName ? (
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") { setIsEditingName(false); setEditedName(character.name); }
                  }}
                  autoFocus
                  maxLength={40}
                  className="flex-1 px-5 focus:outline-none"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    fontWeight: 700,
                    fontSize: "clamp(1.75rem, 3.6vw, 2.6rem)",
                    color: "#0B1A33",
                    background: "#FFFFFF",
                    border: "2px solid rgba(0,101,248,0.30)",
                    borderRadius: 16,
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                    lineHeight: 1.2,
                    paddingTop: 14,
                    paddingBottom: 14,
                  }}
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={isUpdating}
                  className="px-4 py-2 transition active:scale-[0.98]"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 13,
                    border: "none",
                    borderRadius: 999,
                    boxShadow: "0px 3px 0px #073B8E",
                    cursor: "pointer",
                  }}
                >
                  {isUpdating ? "Saving…" : "Save"}
                </button>
              </div>
            ) : (
              <h1
                className="inline-flex items-center gap-3 mb-3"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: "clamp(2rem, 4.6vw, 3.4rem)",
                  letterSpacing: "-0.02em",
                  textTransform: "uppercase",
                  lineHeight: 1.0,
                  margin: 0,
                }}
              >
                <span>{character.name || "Unnamed"}</span>
                {!isPublic && (
                  <button
                    type="button"
                    onClick={() => { setEditedName(character.name || ""); setIsEditingName(true); }}
                    aria-label="Edit name"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      background: "#FFFFFF",
                      color: "#0065F8",
                      border: "2px solid rgba(0,101,248,0.18)",
                      borderRadius: 12,
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                      cursor: "pointer",
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.651L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                    </svg>
                  </button>
                )}
              </h1>
            )}

            {/* Description */}
            {character.description &&
              character.description !== "GENERATING" &&
              !character.description.startsWith("Unable to generate") &&
              !character.description.includes("**") &&
              character.description.length <= 600 && (
                <p
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    color: "#3A4A63",
                    fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)",
                    fontWeight: 500,
                    lineHeight: 1.6,
                    margin: "0 0 28px 0",
                    maxWidth: "56ch",
                  }}
                >
                  {character.description}
                </p>
              )}

            {/* Stat chips */}
            <div className="flex flex-wrap gap-2 mb-8 mt-1">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: "#FFFFFF",
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.04em",
                  border: "2px solid rgba(0,101,248,0.18)",
                  borderRadius: 999,
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                }}
              >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.3" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                </svg>
                Featured in {featuredCount} {featuredCount === 1 ? "story" : "stories"}
              </span>
              {hasAnimated && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "#FFFFFF",
                    color: "#5A6B85",
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.04em",
                    border: "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 999,
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                  }}
                >
                  <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
                  </svg>
                  Animated
                </span>
              )}
            </div>

            {/* Primary CTA */}
            {!isPublic && (
              <button
                type="button"
                onClick={() => navigate(`/create-story?character=${character.id}`)}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 transition-all active:scale-[0.98]"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: "#0065F8",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 17,
                  border: "none",
                  borderRadius: 999,
                  boxShadow: "0px 5px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                  maxWidth: "100%",
                }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Create a Story with {character.name || "this character"}
              </button>
            )}

            {/* Public viewer CTA */}
            {isPublic && (
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 transition-all active:scale-[0.98]"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: "#0065F8",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 17,
                  border: "none",
                  borderRadius: 999,
                  boxShadow: "0px 5px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
              >
                Make your own character on Pixley
              </button>
            )}

            {/* Secondary action toolbar */}
            {!isPublic && (
              <div className="flex flex-wrap gap-3 sm:gap-4 mt-10" style={{ rowGap: 14 }}>
                {[
                  {
                    key: "edit",
                    label: "Edit",
                    icon: (
                      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.651L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                      </svg>
                    ),
                    onClick: () => navigate(`/character-lab?id=${characterId}`),
                  },
                  {
                    key: "share",
                    label: "Share",
                    icon: (
                      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    ),
                    onClick: async () => {
                      trackContentShare?.('character', characterId);
                      // Ensure a share_token exists so the link resolves (owner only).
                      if (character && !character.share_token) {
                        try {
                          const resp = await apiClient.setCharacterSharing(characterId, true);
                          if (resp?.status && resp?.data?.share_token) {
                            setCharacter((prev) => ({ ...prev, share_token: resp.data.share_token }));
                          }
                        } catch (e) { logger.error('Failed to enable character sharing:', e); }
                      }
                      setIsShareModalOpen(true);
                    },
                  },
                  {
                    key: "download",
                    label: isPremium ? "Download" : "Download (Premium)",
                    icon: (
                      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    ),
                    onClick: handleDownloadCharacter,
                  },
                ].map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={b.onClick}
                    className="inline-flex items-center gap-2 px-5 py-2.5 transition-all active:scale-[0.98]"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      background: "#FFFFFF",
                      color: "#334155",
                      fontWeight: 700,
                      fontSize: 13,
                      border: "2px solid rgba(11,26,51,0.08)",
                      borderRadius: 999,
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ color: "#0065F8" }}>{b.icon}</span>
                    {b.label}
                  </button>
                ))}
                {/* Delete — keep visually distinct (rose) but on-brand */}
                <button
                  type="button"
                  onClick={() => requireParentalVerification('delete this character', handleDeleteCharacter)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 transition-all active:scale-[0.98]"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "#FFFFFF",
                    color: "#B91C1C",
                    fontWeight: 700,
                    fontSize: 13,
                    border: "2px solid rgba(220,38,38,0.22)",
                    borderRadius: 999,
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                    cursor: "pointer",
                  }}
                >
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── FEATURED IN STORIES (shelf) ── */}
        {!isPublic && (
          <section className="mt-14">
            <div className="flex items-end justify-between mb-3">
              <div>
                <span
                  className="inline-block mb-0.5"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    color: "#0065F8",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  Adventures
                </span>
                <h2
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    color: "#0B1A33",
                    fontWeight: 700,
                    fontSize: 22,
                    letterSpacing: "-0.01em",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  Featured in {character.name || "their"} stories
                </h2>
              </div>
            </div>

            <StoryShelf
              stories={characterStories}
              onStoryClick={(s) => {
                sessionStorage.setItem("autoplay_video_story", "true");
                navigate(`/stories/${s.id}`);
              }}
              onCreate={() => navigate(`/create-story?character=${character.id}`)}
            />
          </section>
        )}
      </div>

      {/* Share Modal */}
      {!isPublic && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={withReferral(`${window.location.origin}/share/character/${characterId}${character?.share_token ? `?token=${encodeURIComponent(character.share_token)}` : ''}`, user?.id)}
          type="character"
        />
      )}

      {/* Paywall Modal for non-premium users */}
      {!isPublic && (
        <PaywallModal
          isOpen={isPaywallModalOpen}
          onClose={() => setIsPaywallModalOpen(false)}
          starCost={0}
          currentStars={0}
          contentType="download"
          apiClient={apiClient}
          user={user}
        />
      )}
    </div>
  );
};

export default CharacterDetail;
